import { inject, Signal } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import {
  PostApiService,
  ImportByUrlPayload,
} from '../services/post-api.service';
import { UserApiService } from '../services/user-api.service';
import { normalizeCategoriesResponse } from '../normalize/post.normalize';

import * as postActions from 'src/app/core/actions/post.actions';
import { NomMassUpdateOperation } from 'src/app/core/constants';
import { CategoriesMap } from 'src/app/core/services/feed.service';
import { Post, PostSynopsis } from 'src/app/core/models/core.models';
import { ToastService } from '@services/toast.service';
import { realPostId } from 'src/app/shared/utils/shadow-post.utils';
import { FeedPostsService } from 'src/app/core/services/feed-posts.service';

import { firstValueFrom } from 'rxjs';

export function injectImportByUrlMutation() {
  const postApi = inject(PostApiService);

  return injectMutation(() => ({
    mutationFn: (payload: ImportByUrlPayload) =>
      firstValueFrom(postApi.importByUrl(payload)),
  }));
}

// ─── The details pane's post ──────────────────────────────────────────────
// A different resource from the list entry of the same post: `GET /post/:id`
// answers the full record (content, synopsis, translation, quick-story report),
// which is why the pane re-requests a post the list already holds.

export interface PostDetailsScope {
  postId: string;
  feedId: string;
}

const POST_DETAILS_ROOT_KEY = ['post', 'details'] as const;

export const postDetailsQueryKey = (scope: PostDetailsScope | null) =>
  [
    ...POST_DETAILS_ROOT_KEY,
    scope?.postId ?? null,
    scope?.feedId ?? null,
  ] as const;

/**
 * One post across every feed scope it has been fetched under. The patches below
 * target this rather than an exact key: several of the mutations never learn the
 * `feedId` (the synopsis, translation and machine-translate calls only carry a
 * post id), and an entry the user is not looking at is a harmless no-op.
 */
const postDetailsFilter = (postId: string) => ({
  queryKey: [...POST_DETAILS_ROOT_KEY, postId],
});

/** GET /post/:id - the details pane's copy of one post. */
export function injectPostDetailsQuery(scope: Signal<PostDetailsScope | null>) {
  const postApi = inject(PostApiService);

  return injectQuery(() => ({
    queryKey: postDetailsQueryKey(scope()),
    queryFn: () => {
      const { postId, feedId } = scope()!;

      return firstValueFrom(postApi.getPost(postId, feedId));
    },
    enabled: !!scope()?.postId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

/**
 * Apply `update` to the cached post wherever it is held. A cache miss is a no-op.
 */
function patchPostDetails(
  queryClient: QueryClient,
  postId: string,
  update: (post: Post) => Post,
): void {
  queryClient.setQueriesData<Post>(postDetailsFilter(postId), (post) =>
    post ? update(post) : post,
  );
}

/** The common case: the response *is* the new post (`posts[post.id] = post`). */
function setPostDetails(queryClient: QueryClient, post: Post): void {
  patchPostDetails(queryClient, post.id, () => post);
}

/** The language entry holding the saved synopsis, replaced in place. */
function replaceSynopsis(synopsis: any, postSynopsis: PostSynopsis): any {
  return Object.fromEntries(
    Object.entries(synopsis ?? {}).map(([languageId, entry]) => [
      languageId,
      (entry as PostSynopsis)?.id === postSynopsis.id ? postSynopsis : entry,
    ]),
  );
}

/** The deleted synopsis' language entry, dropped. */
function withoutSynopsis(synopsis: any, id: number): any {
  return Object.fromEntries(
    Object.entries(synopsis ?? {}).filter(
      ([, entry]) => (entry as PostSynopsis)?.id !== id,
    ),
  );
}

// ─── Per-post writes ──────────────────────────────────────────────────────
// A write repaints two caches. `patchPost` (the list) fans out through
// `mergePostUpdate`, so the canonical entry *and* every `${realId}__shadow__*`
// copy repaint - the whole point in a threaded feed. `patchPostDetails` (above)
// has no fan-out: that cache is real-id keyed.
//
// The patches live in the hook, **not** at the call site: the sentiment dialog and
// the categorize dialog close before their response lands, and per-call
// `mutate(vars, {onSuccess})` callbacks are dropped when the caller is destroyed.

export function injectCreateSynopsisMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      languageId,
      title,
      synopsis,
      feedId,
    }: {
      postId: string;
      languageId: number;
      title: string;
      synopsis: string;
      feedId: string;
    }) =>
      firstValueFrom(
        postApi.createSynopsis(postId, languageId, title, synopsis, feedId),
      ),
    onSuccess: (post) => setPostDetails(queryClient, post),
  }));
}

export function injectGenerateAISynopsisMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      languageId,
    }: {
      postId: string;
      languageId: number;
    }) => firstValueFrom(postApi.generateAISynopsis(postId, languageId)),
    onSuccess: (response) => setPostDetails(queryClient, response.post[0]),
  }));
}

export function injectUpdateSynopsisMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      id,
      title,
      synopsis,
    }: {
      postId: string;
      id: number;
      title: string;
      synopsis: string;
    }) => firstValueFrom(postApi.updateSynopsis(postId, id, title, synopsis)),
    // The response carries the saved synopsis, not the post: replace the
    // language entry that holds it and leave the rest alone.
    onSuccess: (postSynopsis, { postId }) =>
      patchPostDetails(queryClient, postId, (post) => ({
        ...post,
        synopsis: replaceSynopsis(post.synopsis, postSynopsis),
      })),
  }));
}

export function injectDeleteSynopsisMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: ({ postId, id }: { postId: string; id: number }) =>
      firstValueFrom(postApi.deleteSynopsis(postId, id)),
    onSuccess: (_result, { postId, id }) =>
      patchPostDetails(queryClient, postId, (post) => ({
        ...post,
        synopsis: withoutSynopsis(post.synopsis, id),
      })),
  }));
}

/** Saves a manually edited translation. */
export function injectUpdatePostTranslationMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      languageId,
      title,
      content,
    }: {
      postId: string;
      languageId: number;
      title: string;
      content: string;
    }) =>
      firstValueFrom(
        postApi.updatePostTranslation(postId, languageId, title, content),
      ),
    onSuccess: (post) => {
      setPostDetails(queryClient, post);
      toastService.showSuccess(
        translateService.instant(
          'post-actions.msg.content-translation-saved-successfully',
        ),
      );
    },
  }));
}

/** Machine translation of the post's title + content. */
export function injectTranslatePostMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      languageId,
      title,
      content,
    }: {
      postId: string;
      languageId: number;
      title: string;
      content: string;
    }) =>
      firstValueFrom(postApi.translatePost(postId, languageId, title, content)),
    // The machine translation is not saved yet, so only the one language entry
    // of the pane's copy moves.
    onSuccess: (translation, { postId, languageId }) =>
      patchPostDetails(queryClient, postId, (post) => ({
        ...post,
        translation: {
          ...(post.translation ?? {}),
          [languageId]: {
            title: translation.title,
            content: translation.content,
          },
        },
      })),
  }));
}

export function injectSetPostSentimentMutation() {
  const postApi = inject(PostApiService);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    // Defensive: never let a display-only shadow id reach the backend
    mutationFn: ({
      feedId,
      postId,
      sentiment,
    }: {
      feedId: string;
      postId: string;
      sentiment: number;
    }) =>
      firstValueFrom(
        postApi.setPostSentiment(feedId, realPostId(postId), sentiment),
      ),
    onSuccess: (response) => feedPosts.patchPost(response.post[0]),
  }));
}

/** Runs one algorithm on one post. */
export function injectApplyPostAlgorithmMutation() {
  const postApi = inject(PostApiService);
  const store = inject(Store);
  const queryClient = inject(QueryClient);
  const feedPosts = inject(FeedPostsService);
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      postId,
      algorithmId,
      algorithmParams,
    }: {
      feedId: string;
      postId: string;
      algorithmId: number;
      algorithmParams: any;
    }) =>
      firstValueFrom(
        postApi.applyPostAlgorithm(
          feedId,
          postId,
          algorithmId,
          algorithmParams,
        ),
      ),
    onSuccess: (posts) => {
      feedPosts.patchPosts(posts);
      for (const post of posts) {
        setPostDetails(queryClient, post);
      }
      store.dispatch(postActions.applyPostAlgorithmSuccess({ posts }));
      toastService.showSuccess(
        translateService.instant(
          'post-actions.msg.algorithm-processing-queued-successfully',
        ),
      );
    },
  }));
}

/** Runs one algorithm on the feed's whole selection. */
export function injectApplySelectedPostsAlgorithmMutation() {
  const postApi = inject(PostApiService);
  const store = inject(Store);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      algorithmId,
      algorithmParams,
    }: {
      feedId: string;
      algorithmId: number;
      algorithmParams: any;
    }) =>
      firstValueFrom(
        postApi.applySelectedPostsAlgorithm(
          feedId,
          algorithmId,
          algorithmParams,
        ),
      ),
    onSuccess: (posts, { feedId }) => {
      const patched = feedPosts.patchPosts(posts.flat());

      store.dispatch(
        postActions.applySelectedPostsAlgorithmSuccess({
          feedId,
          postIds: patched,
        }),
      );
    },
  }));
}

export function injectCreateQuickStoryReportMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      languageId,
      feedId,
    }: {
      postId: string;
      languageId: number;
      feedId: string;
    }) =>
      firstValueFrom(
        postApi.createQuickStoryReport(postId, languageId, feedId),
      ),
    onSuccess: (post) => {
      feedPosts.patchPost(post);
      setPostDetails(queryClient, post);
    },
  }));
}

export function injectUpdateQuickStoryReportMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      languageId,
      feedId,
      html,
    }: {
      postId: string;
      languageId: number;
      feedId: string;
      html: string;
    }) =>
      firstValueFrom(
        postApi.updateQuickStoryReport(postId, languageId, feedId, html),
      ),
    onSuccess: (post) => {
      feedPosts.patchPost(post);
      setPostDetails(queryClient, post);
    },
  }));
}

export function injectTransitionToWorkflowStageMutation() {
  const postApi = inject(PostApiService);
  const queryClient = inject(QueryClient);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    // Defensive: never let a display-only shadow id reach the backend
    mutationFn: ({
      postId,
      workflowId,
      workflowStageId,
      feedId,
    }: {
      postId: string;
      workflowId: string;
      workflowStageId: number;
      feedId: string;
    }) =>
      firstValueFrom(
        postApi.transitionToWorkflowStage(
          realPostId(postId),
          workflowId,
          workflowStageId,
          feedId,
        ),
      ),
    onSuccess: (post) => {
      feedPosts.patchWorkflows(post);
      patchPostDetails(queryClient, post.id, (current) => ({
        ...current,
        workflows: post.workflows,
      }));
    },
  }));
}

/** The post card's inline category tree. */
export function injectUpdatePostsUserCategoriesMutation() {
  const postApi = inject(PostApiService);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    mutationFn: ({
      postIds,
      categories,
      updateOperation,
    }: {
      postIds: string[];
      categories: CategoriesMap;
      updateOperation: NomMassUpdateOperation;
    }) =>
      firstValueFrom(
        postApi.assignCategoryTree(categories, postIds, updateOperation),
      ),
    onSuccess: (response, { postIds }) =>
      feedPosts.patchUserCategories(
        postIds,
        normalizeCategoriesResponse(response?.categories, postIds),
      ),
  }));
}

export function injectSetPostRelatedPostsMutation() {
  const postApi = inject(PostApiService);
  const feedPosts = inject(FeedPostsService);

  return injectMutation(() => ({
    mutationFn: ({
      postId,
      relatedPostIds,
      feedId,
    }: {
      postId: string;
      relatedPostIds: string[];
      feedId: string;
    }) =>
      firstValueFrom(postApi.addRelatedPosts(postId, relatedPostIds, feedId)),
    onSuccess: (post) => feedPosts.patchPost(post),
  }));
}

// ─── Selection ────────────────────────────────────────────────────────────
// Fire-and-forget: the POST only mirrors the selection server-side and its response
// is never read. The visible change is the reducer's `selected` flag plus
// `feed.count_selected_posts`, so call sites keep dispatching `togglePostSelect` /
// `selectAllPosts`.

/**
 * Select / unselect the posts the caller passes. Shadow ids are stripped —
 * `${realId}__shadow__${leafId}` is a display-only thread copy the backend does
 * not know.
 */
export function injectTogglePostsSelectionMutation() {
  const userApi = inject(UserApiService);

  return injectMutation(() => ({
    mutationFn: ({
      userId,
      feedId,
      posts,
      selected,
    }: {
      userId: string;
      feedId: string;
      posts: Post[];
      selected: boolean;
    }) => {
      const realPosts = posts.map((post) => ({
        ...post,
        id: realPostId(post.id),
      }));

      return firstValueFrom(
        selected
          ? userApi.selectPosts(userId, feedId, realPosts)
          : userApi.deSelectPosts(userId, feedId, realPosts),
      );
    },
  }));
}

// ─── The categorize dialog's tree ─────────────────────────────────────────
// The fetched tree is this query; the checkbox state built on top of it lives in
// `PostUserCategoriesEditorService`.

export function injectPostUserCategoriesQuery(
  postIds: Signal<string[]>,
  selectedPostsFeedId: Signal<string>,
) {
  const postApi = inject(PostApiService);

  return injectQuery(() => ({
    queryKey: ['post', 'user-categories', postIds(), selectedPostsFeedId()],
    queryFn: () =>
      firstValueFrom(
        postApi.getPostCategories(postIds(), selectedPostsFeedId()),
      ),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}
